import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Slider,
} from '@mui/material';
import {
  Delete,
  MoreVert,
  FlipToFront,
  FlipToBack,
  Settings,
} from '@mui/icons-material';
import type { ConnectorElement as ConnectorElementType, CanvasElement, Position, ConnectionPointPosition } from '@/types/thinkspace';
import {
  generateConnectorPath,
  calculateSmartAngularRoute,
  getElementConnectionPoints,
  findNearestConnectionPoint,
} from '@/utils/connectorUtils';
import { ColorPalette } from '@/components/common/ColorPalette';

type ConnectorElementProps = {
  element: ConnectorElementType;
  allElements: CanvasElement[];
  selected: boolean;
  canEdit: boolean;
  onSelect: () => void;
  onUpdate?: (updates: Partial<ConnectorElementType>) => void;
  onDelete: () => void;
  onBringToFront: () => void;
  onSendToBack: () => void;
  onBringForward?: () => void;
  onSendBackward?: () => void;
};

export const ConnectorElement = ({
  element,
  allElements,
  selected,
  canEdit,
  onSelect,
  onUpdate,
  onDelete,
  onBringToFront,
  onSendToBack,
}: ConnectorElementProps) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [propsOpen, setPropsOpen] = useState(false);
  const [strokeColor, setStrokeColor] = useState(element.strokeColor);
  const [strokeWidth, setStrokeWidth] = useState(element.strokeWidth);

  // Endpoint dragging state
  const [draggingEndpoint, setDraggingEndpoint] = useState<'start' | 'end' | null>(null);
  const [dragPos, setDragPos] = useState<Position | null>(null);
  const [snapTarget, setSnapTarget] = useState<{
    elementId: string;
    position: ConnectionPointPosition;
    point: Position;
  } | null>(null);

  // Handle endpoint drag
  const handleEndpointMouseDown = useCallback((endpoint: 'start' | 'end', e: React.MouseEvent) => {
    if (!canEdit) return;
    e.stopPropagation();
    e.preventDefault();
    setDraggingEndpoint(endpoint);
    // Initialize drag position to current endpoint position
    const currentPos = endpoint === 'start' ? element.startPosition : element.endPosition;
    setDragPos(currentPos);
  }, [canEdit, element.startPosition, element.endPosition]);

  // Handle mouse move during drag
  useEffect(() => {
    if (!draggingEndpoint) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Find the canvas container by data attribute
      const canvasContainer = document.querySelector('[data-canvas-container]') as HTMLElement;
      if (!canvasContainer) return;

      const rect = canvasContainer.getBoundingClientRect();
      const scrollLeft = canvasContainer.scrollLeft;
      const scrollTop = canvasContainer.scrollTop;

      const pos: Position = {
        x: e.clientX - rect.left + scrollLeft,
        y: e.clientY - rect.top + scrollTop,
      };

      setDragPos(pos);

      // Check for snap points
      const SNAP_DISTANCE = 25;
      let foundSnap = null;
      for (const el of allElements) {
        if (el.type === 'connector') continue;
        const nearest = findNearestConnectionPoint(pos, el, SNAP_DISTANCE);
        if (nearest) {
          foundSnap = {
            elementId: el.id,
            position: nearest.position,
            point: nearest.point,
          };
          break;
        }
      }
      setSnapTarget(foundSnap);
    };

    const handleMouseUp = () => {
      if (draggingEndpoint && dragPos) {
        // Finalize the drag - update the connector
        const finalPos = snapTarget?.point || dragPos;
        const connection = snapTarget ? {
          elementId: snapTarget.elementId,
          position: snapTarget.position,
        } : null;

        if (draggingEndpoint === 'start') {
          onUpdate?.({
            startPosition: finalPos,
            startConnection: connection,
            routingPoints: [], // Will be recalculated
          });
        } else {
          onUpdate?.({
            endPosition: finalPos,
            endConnection: connection,
            routingPoints: [], // Will be recalculated
          });
        }
      }

      setDraggingEndpoint(null);
      setDragPos(null);
      setSnapTarget(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingEndpoint, dragPos, snapTarget, allElements, onUpdate]);

  // Calculate actual positions based on connections
  const { baseStartPos, baseEndPos } = useMemo(() => {
    let start = element.startPosition;
    let end = element.endPosition;

    // If connected to elements, calculate positions from connection points
    if (element.startConnection) {
      const startElement = allElements.find(el => el.id === element.startConnection?.elementId);
      if (startElement) {
        const points = getElementConnectionPoints(startElement);
        if (points && element.startConnection.position in points) {
          start = points[element.startConnection.position];
        }
      }
    }

    if (element.endConnection) {
      const endElement = allElements.find(el => el.id === element.endConnection?.elementId);
      if (endElement) {
        const points = getElementConnectionPoints(endElement);
        if (points && element.endConnection.position in points) {
          end = points[element.endConnection.position];
        }
      }
    }

    return { baseStartPos: start, baseEndPos: end };
  }, [element, allElements]);

  // Calculate effective positions (accounting for drag state)
  const startPos = draggingEndpoint === 'start' && dragPos ? dragPos : baseStartPos;
  const endPos = draggingEndpoint === 'end' && dragPos ? dragPos : baseEndPos;

  // Calculate routing points
  const routingPoints = useMemo(() => {
    // Get elements for smart routing
    const startElement = element.startConnection
      ? allElements.find(el => el.id === element.startConnection?.elementId)
      : null;
    const endElement = element.endConnection
      ? allElements.find(el => el.id === element.endConnection?.elementId)
      : null;

    // Always recalculate during drag, otherwise use stored or calculate new
    if (draggingEndpoint) {
      return calculateSmartAngularRoute(startPos, endPos, startElement, endElement);
    }

    return element.routingPoints.length > 0
      ? element.routingPoints
      : calculateSmartAngularRoute(startPos, endPos, startElement, endElement);
  }, [element, allElements, startPos, endPos, draggingEndpoint]);

  // Calculate bounding box for the SVG
  const bounds = useMemo(() => {
    const allPoints = [startPos, ...routingPoints, endPos];
    const xs = allPoints.map(p => p.x);
    const ys = allPoints.map(p => p.y);
    const minX = Math.min(...xs) - 20;
    const minY = Math.min(...ys) - 20;
    const maxX = Math.max(...xs) + 20;
    const maxY = Math.max(...ys) + 20;
    return { minX, minY, width: maxX - minX, height: maxY - minY };
  }, [startPos, endPos, routingPoints]);

  // Generate SVG path
  const pathData = useMemo(() => {
    // Adjust positions relative to SVG viewport
    const adjustedStart = { x: startPos.x - bounds.minX, y: startPos.y - bounds.minY };
    const adjustedEnd = { x: endPos.x - bounds.minX, y: endPos.y - bounds.minY };
    const adjustedRouting = routingPoints.map(p => ({
      x: p.x - bounds.minX,
      y: p.y - bounds.minY,
    }));
    return generateConnectorPath(adjustedStart, adjustedEnd, adjustedRouting);
  }, [startPos, endPos, routingPoints, bounds]);

  // Midpoint for menu button
  const midPoint = useMemo(() => {
    const allPoints = [startPos, ...routingPoints, endPos];
    const midIndex = Math.floor(allPoints.length / 2);
    return allPoints[midIndex];
  }, [startPos, endPos, routingPoints]);

  return (
    <>
      {/* SVG container positioned at bounds */}
      <Box
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
        sx={{
          position: 'absolute',
          left: bounds.minX,
          top: bounds.minY,
          width: bounds.width,
          height: bounds.height,
          pointerEvents: 'none',
          zIndex: element.zIndex,
        }}
      >
        <svg
          width={bounds.width}
          height={bounds.height}
          style={{ overflow: 'visible' }}
        >
          {/* Invisible wider path for easier selection */}
          <path
            d={pathData}
            fill="none"
            stroke="transparent"
            strokeWidth={Math.max(element.strokeWidth + 10, 15)}
            style={{ cursor: canEdit ? 'pointer' : 'default', pointerEvents: 'stroke' }}
            onClick={(e) => {
              e.stopPropagation();
              onSelect();
            }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              if (canEdit) {
                setPropsOpen(true);
              }
            }}
          />
          {/* Visible path */}
          <path
            d={pathData}
            fill="none"
            stroke={element.strokeColor}
            strokeWidth={element.strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ pointerEvents: 'none' }}
          />
          {/* Selection highlight */}
          {selected && (
            <path
              d={pathData}
              fill="none"
              stroke="#0f2ccf"
              strokeWidth={element.strokeWidth + 4}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={0.3}
              style={{ pointerEvents: 'none' }}
            />
          )}
          {/* Arrow head at end */}
          <defs>
            <marker
              id={`arrowhead-${element.id}`}
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon
                points="0 0, 10 3.5, 0 7"
                fill={element.strokeColor}
              />
            </marker>
          </defs>
        </svg>
      </Box>

      {/* Endpoint circles when selected - rendered separately with high z-index for always-clickable */}
      {selected && canEdit && (
        <>
          {/* Start endpoint */}
          <Box
            onMouseDown={(e) => handleEndpointMouseDown('start', e as unknown as React.MouseEvent)}
            sx={{
              position: 'absolute',
              left: startPos.x - 8,
              top: startPos.y - 8,
              width: 16,
              height: 16,
              borderRadius: '50%',
              backgroundColor: draggingEndpoint === 'start' ? '#1976d2' : '#0f2ccf',
              border: '2px solid white',
              cursor: 'grab',
              zIndex: 10000, // Always on top of other elements
              '&:hover': {
                transform: 'scale(1.2)',
              },
            }}
          />
          {/* End endpoint */}
          <Box
            onMouseDown={(e) => handleEndpointMouseDown('end', e as unknown as React.MouseEvent)}
            sx={{
              position: 'absolute',
              left: endPos.x - 8,
              top: endPos.y - 8,
              width: 16,
              height: 16,
              borderRadius: '50%',
              backgroundColor: draggingEndpoint === 'end' ? '#1976d2' : '#0f2ccf',
              border: '2px solid white',
              cursor: 'grab',
              zIndex: 10000, // Always on top of other elements
              '&:hover': {
                transform: 'scale(1.2)',
              },
            }}
          />
        </>
      )}

      {/* Snap indicator when dragging endpoint near a connection point */}
      {draggingEndpoint && snapTarget && (
        <Box
          sx={{
            position: 'absolute',
            left: snapTarget.point.x - 10,
            top: snapTarget.point.y - 10,
            width: 20,
            height: 20,
            borderRadius: '50%',
            backgroundColor: 'success.main',
            border: '3px solid white',
            boxShadow: '0 0 8px rgba(76, 175, 80, 0.6)',
            zIndex: element.zIndex + 10,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Menu button positioned at midpoint */}
      {canEdit && selected && !propsOpen && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
          }}
          sx={{
            position: 'absolute',
            left: midPoint.x - 12,
            top: midPoint.y - 12,
            backgroundColor: 'white',
            boxShadow: 1,
            zIndex: element.zIndex + 1,
            '&:hover': { backgroundColor: 'grey.100' },
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={() => { setMenuAnchor(null); setPropsOpen(true); }}>
          <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
          <ListItemText>Properties</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onBringToFront(); }}>
          <ListItemIcon><FlipToFront fontSize="small" /></ListItemIcon>
          <ListItemText>Bring to Front</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onSendToBack(); }}>
          <ListItemIcon><FlipToBack fontSize="small" /></ListItemIcon>
          <ListItemText>Send to Back</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onDelete(); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Properties Dialog */}
      <Dialog
        open={propsOpen}
        onClose={() => setPropsOpen(false)}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <DialogTitle>Connector Properties</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, minWidth: 300 }}>
            <Typography gutterBottom>Line Color</Typography>
            <Box sx={{ mb: 3 }}>
              <ColorPalette
                value={strokeColor}
                onChange={setStrokeColor}
              />
            </Box>

            <Typography gutterBottom>Line Width: {strokeWidth}px</Typography>
            <Slider
              value={strokeWidth}
              onChange={(_, value) => setStrokeWidth(value as number)}
              min={1}
              max={10}
              step={1}
              valueLabelDisplay="auto"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPropsOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              onUpdate?.({ strokeColor, strokeWidth });
              setPropsOpen(false);
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
